import React from 'react';
import PropTypes from 'prop-types';
import {
  TableCell,
  TableRow,
  Typography,
  IconButton,
  Collapse,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Grid,
  Paper,
} from '@material-ui/core';
import {
  ExpandMore,
  ExpandLess,
  Edit as EditIcon,
  Group as GroupIcon,
  MoreVert as MoreVertIcon,
  TrendingUp as ScaleIcon,
  EmojiEvents as BonusIcon,
  Warning as PenaltyIcon,
  Payment as PaymentIcon,
  AttachMoney as MoneyIcon,
  Business as DistributorIcon,
} from '@material-ui/icons';
import { DateTime } from 'luxon';
import { ModalGerenciarParceiros } from '../modals/ModalGerenciarParceiros';
import { useResumoStyles } from './ListagemPlanosTableRow.styles';

export function ListagemPlanosTableRow(props) {
  const { planData, classes, onEdit } = props;
  const resumoClasses = useResumoStyles();
  const [modalGerenciarOpen, setModalGerenciarOpen] = React.useState(false);
  const [collapseOpen, setCollapseOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleRowClick = () => {
    setCollapseOpen(prev => !prev);
  };

  const handleMenuClick = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = event => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleGerenciarParceirosClick = event => {
    handleMenuClose(event);
    setModalGerenciarOpen(true);
  };

  const handleEditClick = event => {
    handleMenuClose(event);
    if (onEdit && planData) {
      const planId = planData.publicId || planData.id;
      onEdit(planId);
    }
  };

  const handleCloseGerenciarModal = () => {
    setModalGerenciarOpen(false);
  };

  const handleToggleCollapse = event => {
    event.stopPropagation();
    setCollapseOpen(prev => !prev);
  };

  const formatDate = dateString => {
    if (!dateString) {
      return '-';
    }
    try {
      return DateTime.fromISO(dateString).toFormat('dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const formatPenaltyReason = reason => {
    const reasons = {
      non_payment: 'Inadimplência',
      churn: 'Cancelamento',
    };
    return reasons[reason] || reason;
  };

  const faixasCount = planData?.comissionScales?.length || 0;
  const bonusCount = planData?.comissionBonus?.length || 0;
  const penalidadesCount = planData?.comissionPenalties?.length || 0;
  const payment = planData?.comissionPayment || {};
  const hasPaymentInfo = payment.hasInitialPayment || payment.hasInvoicePayment || payment.hasRecurrence;
  const distributors = planData?.tarifaDistribuidoras || [];
  const distributorsCount = distributors.length;

  // Fixed value calculation info
  const hasFixedValue =
    planData?.calculationType === 'fixed' ||
    planData?.fixedValue !== undefined ||
    planData?.comissionValue !== undefined ||
    planData?.comissionPercentage !== undefined;
  const fixedValue = planData?.fixedValue || planData?.comissionValue || planData?.comissionPercentage;
  const fixedType = planData?.fixedType || planData?.comissionType || 'fixed';
  const isScaled = faixasCount > 0;
  const hasCalculationInfo = hasFixedValue || isScaled;

  const formatCurrency = value => {
    if (!value && value !== 0) {
      return 'R$ 0,00';
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(numValue)) {
      return 'R$ 0,00';
    }
    return numValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <>
      <TableRow className={classes.tableRow} onClick={handleRowClick} style={{ cursor: 'pointer' }}>
        <TableCell width="25%" className={classes.tableCell}>
          <Typography variant="caption" style={{ fontSize: '13px' }}>
            {planData?.name || '-'}
          </Typography>
        </TableCell>

        <TableCell width="27%" className={classes.tableCell}>
          <Typography variant="caption" style={{ fontSize: '13px' }}>
            {planData?.description || '-'}
          </Typography>
        </TableCell>

        <TableCell width="13%" className={classes.tableCell}>
          <Typography variant="caption" style={{ fontSize: '13px' }}>
            {formatDate(planData?.createdAt)}
          </Typography>
        </TableCell>

        <TableCell width="18%" className={classes.tableCell}>
          <Box className={resumoClasses.resumoContainer}>
            {hasCalculationInfo && (
              <Tooltip
                title={
                  isScaled
                    ? `${faixasCount} faixa${faixasCount > 1 ? 's' : ''} de escalonamento`
                    : 'Valor Fixo configurado'
                }
                arrow
              >
                <span className={`${resumoClasses.resumoItem} ${resumoClasses.scaleItem}`}>
                  {isScaled ? (
                    <ScaleIcon className={resumoClasses.iconSmall} />
                  ) : (
                    <MoneyIcon className={resumoClasses.iconSmall} />
                  )}
                </span>
              </Tooltip>
            )}
            {bonusCount > 0 && (
              <Tooltip title={`${bonusCount} bônus configurado${bonusCount > 1 ? 's' : ''}`} arrow>
                <span className={`${resumoClasses.resumoItem} ${resumoClasses.bonusItem}`}>
                  <BonusIcon className={resumoClasses.iconSmall} />
                </span>
              </Tooltip>
            )}
            {penalidadesCount > 0 && (
              <Tooltip
                title={`${penalidadesCount} penalidade${penalidadesCount > 1 ? 's' : ''} configurada${
                  penalidadesCount > 1 ? 's' : ''
                }`}
                arrow
              >
                <span className={`${resumoClasses.resumoItem} ${resumoClasses.penaltyItem}`}>
                  <PenaltyIcon className={resumoClasses.iconSmall} />
                </span>
              </Tooltip>
            )}
            {hasPaymentInfo && (
              <Tooltip title="Pagamento configurado" arrow>
                <span className={`${resumoClasses.resumoItem} ${resumoClasses.paymentItem}`}>
                  <PaymentIcon className={resumoClasses.iconSmall} />
                </span>
              </Tooltip>
            )}
            {!hasCalculationInfo && bonusCount === 0 && penalidadesCount === 0 && !hasPaymentInfo && (
              <Typography className={resumoClasses.emptyState}>Não configurado</Typography>
            )}
          </Box>
        </TableCell>

        <TableCell width="7%" className={classes.tableCell}>
          <IconButton size="small" onClick={handleMenuClick} className={classes.actionIconButton}>
            <MoreVertIcon />
          </IconButton>
          <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose} onClick={e => e.stopPropagation()}>
            <MenuItem onClick={handleEditClick}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Editar Plano" />
            </MenuItem>
            <MenuItem onClick={handleGerenciarParceirosClick}>
              <ListItemIcon>
                <GroupIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Gerenciar Parceiros" />
            </MenuItem>
          </Menu>
        </TableCell>

        <TableCell width="3%" className={classes.tableCell} onClick={handleToggleCollapse}>
          <IconButton size="small" onClick={handleToggleCollapse}>
            {collapseOpen ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </TableCell>
      </TableRow>

      {collapseOpen && (
        <TableRow>
          <TableCell colSpan={6} style={{ padding: 0 }}>
            <Collapse in={collapseOpen} timeout="auto" unmountOnExit>
              <Box className={resumoClasses.expandedContainer}>
                {!hasCalculationInfo && bonusCount === 0 && penalidadesCount === 0 && !hasPaymentInfo ? (
                  <Typography className={resumoClasses.noDataMessage}>
                    Nenhuma informação adicional disponível.
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {distributorsCount > 0 && (
                      <Grid item xs={12}>
                        <Paper className={`${resumoClasses.card} ${resumoClasses.scaleCard}`} elevation={0}>
                          <Box className={resumoClasses.cardHeader}>
                            <DistributorIcon style={{ color: '#5c6bc0', fontSize: 20 }} />
                            <Typography className={resumoClasses.cardTitle} style={{ color: '#5c6bc0' }}>
                              Distribuidoras ({distributorsCount})
                            </Typography>
                          </Box>
                          <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {distributors.map(dist => (
                              <Box
                                key={dist.id || dist.empresa}
                                style={{
                                  backgroundColor: '#e8eaf6',
                                  color: '#3949ab',
                                  padding: '4px 12px',
                                  borderRadius: 16,
                                  fontSize: 12,
                                  fontWeight: 500,
                                }}
                              >
                                {dist.empresa}
                              </Box>
                            ))}
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <Paper className={`${resumoClasses.card} ${resumoClasses.scaleCard}`} elevation={0}>
                        <Box className={resumoClasses.cardHeader}>
                          {isScaled ? (
                            <ScaleIcon style={{ color: '#1976d2', fontSize: 20 }} />
                          ) : (
                            <MoneyIcon style={{ color: '#1976d2', fontSize: 20 }} />
                          )}
                          <Typography className={resumoClasses.cardTitle} style={{ color: '#1976d2' }}>
                            {isScaled ? 'Faixas de Escalonamento' : 'Cálculo de Comissão'}
                          </Typography>
                        </Box>
                        <Box className={resumoClasses.cardContent}>
                          {isScaled &&
                            planData.comissionScales.map(faixa => (
                              <Box
                                key={`scale-${faixa.minKWhVolume}-${faixa.maxKWhVolume}-${faixa.comissionPercentage}`}
                                className={resumoClasses.cardItem}
                              >
                                <Typography className={resumoClasses.cardItemLabel}>
                                  {faixa.minKWhVolume || 0} - {faixa.maxKWhVolume || '∞'} kWh
                                </Typography>
                                <Typography className={resumoClasses.cardItemValue} style={{ color: '#1976d2' }}>
                                  {(faixa.comissionPercentage * 100).toFixed(2)}%
                                </Typography>
                              </Box>
                            ))}
                          {!isScaled && hasFixedValue && (
                            <Box className={resumoClasses.cardItem}>
                              <Typography className={resumoClasses.cardItemLabel}>
                                {fixedType === 'percentage' || fixedType === 'percent' ? 'Percentual' : 'Valor Fixo'}
                              </Typography>
                              <Typography className={resumoClasses.cardItemValue} style={{ color: '#1976d2' }}>
                                {fixedType === 'percentage' || fixedType === 'percent'
                                  ? `${fixedValue}%`
                                  : formatCurrency(fixedValue)}
                              </Typography>
                            </Box>
                          )}
                          {!isScaled && !hasFixedValue && (
                            <Box className={resumoClasses.emptyCard}>Nenhum cálculo configurado</Box>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper className={`${resumoClasses.card} ${resumoClasses.penaltyCard}`} elevation={0}>
                        <Box className={resumoClasses.cardHeader}>
                          <PenaltyIcon style={{ color: '#c62828', fontSize: 20 }} />
                          <Typography className={resumoClasses.cardTitle} style={{ color: '#c62828' }}>
                            Penalidades
                          </Typography>
                        </Box>
                        <Box className={resumoClasses.cardContent}>
                          {penalidadesCount > 0 ? (
                            planData.comissionPenalties.map(penalidade => (
                              <Box
                                key={`penalty-${penalidade.reason}-${penalidade.value}-${penalidade.type}`}
                                className={resumoClasses.cardItem}
                              >
                                <Typography className={resumoClasses.cardItemLabel}>
                                  {formatPenaltyReason(penalidade.reason)}
                                </Typography>
                                <Typography className={resumoClasses.cardItemValue} style={{ color: '#c62828' }}>
                                  {penalidade.type === 'percentage'
                                    ? `${penalidade.value}%`
                                    : formatCurrency(penalidade.value)}
                                </Typography>
                              </Box>
                            ))
                          ) : (
                            <Box className={resumoClasses.emptyCard}>Nenhuma penalidade configurada</Box>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper className={`${resumoClasses.card} ${resumoClasses.bonusCard}`} elevation={0}>
                        <Box className={resumoClasses.cardHeader}>
                          <BonusIcon style={{ color: '#e65100', fontSize: 20 }} />
                          <Typography className={resumoClasses.cardTitle} style={{ color: '#e65100' }}>
                            Bônus
                          </Typography>
                        </Box>
                        <Box className={resumoClasses.cardContent}>
                          {bonusCount > 0 ? (
                            planData.comissionBonus.map(bonus => (
                              <Box
                                key={`bonus-${bonus.bonusTrigger}-${bonus.value}-${bonus.condition}`}
                                className={resumoClasses.cardItem}
                              >
                                <Typography className={resumoClasses.cardItemLabel}>
                                  {bonus.name || `Gatilho: ${bonus.bonusTrigger}`}
                                </Typography>
                                <Typography className={resumoClasses.cardItemValue} style={{ color: '#e65100' }}>
                                  {formatCurrency(bonus.value)}
                                </Typography>
                              </Box>
                            ))
                          ) : (
                            <Box className={resumoClasses.emptyCard}>Nenhum bônus configurado</Box>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper className={`${resumoClasses.card} ${resumoClasses.paymentCard}`} elevation={0}>
                        <Box className={resumoClasses.cardHeader}>
                          <PaymentIcon style={{ color: '#2e7d32', fontSize: 20 }} />
                          <Typography className={resumoClasses.cardTitle} style={{ color: '#2e7d32' }}>
                            Pagamento
                          </Typography>
                        </Box>
                        <Box className={resumoClasses.cardContent}>
                          {hasPaymentInfo ? (
                            <>
                              {payment.hasInitialPayment && (
                                <Box className={resumoClasses.cardItem}>
                                  <Typography className={resumoClasses.cardItemLabel}>Pagamento Inicial</Typography>
                                  <Typography className={resumoClasses.cardItemValue} style={{ color: '#2e7d32' }}>
                                    {payment.initialPaymentType === 'fixed'
                                      ? formatCurrency(payment.initialPaymentValue)
                                      : `${payment.initialPaymentValue || '0'}%`}
                                  </Typography>
                                </Box>
                              )}
                              {payment.hasInvoicePayment && (
                                <Box className={resumoClasses.cardItem}>
                                  <Typography className={resumoClasses.cardItemLabel}>
                                    Pagamento de Primeira Fatura
                                  </Typography>
                                  <Typography className={resumoClasses.cardItemValue} style={{ color: '#2e7d32' }}>
                                    {payment.invoicePaymentType === 'fixed'
                                      ? formatCurrency(payment.invoicePaymentValue)
                                      : `${payment.invoicePaymentValue || '0'}%`}
                                  </Typography>
                                </Box>
                              )}
                              {payment.hasRecurrence && (
                                <Box className={resumoClasses.cardItem}>
                                  <Typography className={resumoClasses.cardItemLabel}>Recorrência</Typography>
                                  <Typography className={resumoClasses.cardItemValue} style={{ color: '#2e7d32' }}>
                                    {payment.recurrenceType === 'fixed'
                                      ? `${formatCurrency(payment.recurrenceValue)}/mês`
                                      : `${payment.recurrenceValue || '0'}%/mês`}
                                  </Typography>
                                </Box>
                              )}
                            </>
                          ) : (
                            <Box className={resumoClasses.emptyCard}>Nenhum pagamento configurado</Box>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}

      <ModalGerenciarParceiros open={modalGerenciarOpen} onClose={handleCloseGerenciarModal} planData={planData} />
    </>
  );
}

ListagemPlanosTableRow.propTypes = {
  planData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    publicId: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    createdAt: PropTypes.string,
    comissionScales: PropTypes.arrayOf(
      PropTypes.shape({
        minKWhVolume: PropTypes.number,
        maxKWhVolume: PropTypes.number,
        comissionPercentage: PropTypes.number,
      }),
    ),
    comissionBonus: PropTypes.arrayOf(
      PropTypes.shape({
        bonusTrigger: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        condition: PropTypes.string,
      }),
    ),
    comissionPenalties: PropTypes.arrayOf(
      PropTypes.shape({
        reason: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        type: PropTypes.string,
      }),
    ),
    comissionPayment: PropTypes.shape({
      hasInitialPayment: PropTypes.bool,
      initialPaymentType: PropTypes.string,
      initialPaymentValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      hasInvoicePayment: PropTypes.bool,
      invoicePaymentType: PropTypes.string,
      invoicePaymentValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      hasRecurrence: PropTypes.bool,
      recurrenceType: PropTypes.string,
      recurrenceValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    tarifaDistribuidoras: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        empresa: PropTypes.string,
      }),
    ),
  }).isRequired,
  classes: PropTypes.shape({
    tableRow: PropTypes.string,
    tableCell: PropTypes.string,
  }).isRequired,
  onEdit: PropTypes.func,
};

ListagemPlanosTableRow.defaultProps = {
  onEdit: null,
};
